import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getReviewRatingList,
    addReviewRating,
    editReviewRating,
} from "../../Redux/Actions/ratingReviewPanelAction";
import { columnsReviewRatingList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import ReviewRatingEditModal from "./reviewRatingPanelPopup";
import ReviewRatingViewModal from "./viewReviewRatingPanelPopup";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Button } from "antd";

const ListReviewRating = () => {
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReviewRating, setSelectedReviewRating] = useState(null);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedViewReview, setSelectedViewReview] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const fetchDetails = useCallback(
        (showLoader = true, page = pagination.current, pageSize = pagination.pageSize) => {
            const payload = {
                page,
                limit: pageSize,
                filter: debouncedSearch,
                is_deleted: false,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            console.log("Payload sent:", payload); 
            return dispatch(getReviewRatingList(payload, showLoader)).catch(() => {
                if (showLoader) {
                    toast.error("Failed to fetch review ratings list");
                }
            });
        },
        [dispatch, debouncedSearch, dateRange, pagination.current, pagination.pageSize]
    );

    const reviewRatingState = useSelector((state) => state.ratingReviewPanel || {});
    const reviewRatings = useMemo(() => {
        return reviewRatingState.reviewRatings?.data || [];
    }, [reviewRatingState.reviewRatings]);
    const reviewRatingTotal = useMemo(() => {
        return reviewRatingState.reviewRatings?.total || 0;
    }, [reviewRatingState.reviewRatings]);
    const { loading } = reviewRatingState;

    useEffect(() => {
        fetchDetails(true, pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, fetchDetails]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [debouncedSearch, dateRange]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, total: reviewRatingTotal }));
    }, [reviewRatingTotal]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const openEditModal = useCallback((reviewRating = null) => {
        setSelectedReviewRating(reviewRating);
        setIsModalOpen(true);
    }, []);

    const closeEditModal = () => {
        setSelectedReviewRating(null);
        setIsModalOpen(false);
    };

    const openViewModal = useCallback((review = null) => {
        setSelectedViewReview(review);
        setIsViewModalOpen(true);
    }, []);

    const closeViewModal = () => {
        setSelectedViewReview(null);
        setIsViewModalOpen(false);
    };

    const handleSubmit = async (values) => {
        const prevReviewRatings = reviewRatingState.reviewRatings?.data || [];

        const isDuplicateName = prevReviewRatings.some(
            (review) =>
                review.name === values.name &&
                review._id !== selectedReviewRating?._id
        );

        if (isDuplicateName) {
            toast.error("Name already exists");
            return { success: false };
        }

        try {
            let result;
            if (selectedReviewRating?._id) {
                result = await dispatch(editReviewRating(values, selectedReviewRating._id));
            } else {
                result = await dispatch(addReviewRating(values));
            }

            if (result) {
                // Fixed: Pass current pagination values
                await fetchDetails(false, pagination.current, pagination.pageSize);
                closeEditModal();
                return { success: true };
            }
        } catch (error) {
            toast.error("Failed to save review");
            return { success: false };
        }
    };

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
        // Page reset now handled by useEffect - no need here
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    return (
        <>
            <div style={{ position: "absolute", marginTop: "9.6%", marginLeft: "88.5%" }}>
                <Button
                    style={{ background: "#0D1A52", color: "white", padding: "18px", borderRadius: "20px" }}
                    onClick={() => openEditModal(null)}
                >
                    Add Review
                </Button>
            </div>

            <TableComponent
                pagination={pagination}
                setState={setSearchQuery}
                dataSource={reviewRatings}
                onCalendarClick={() => setIsDatePopupVisible(true)}
                onChange={handleTableChange}
                h3heading="Review Rating Panel"
                columns={columnsReviewRatingList(
                    pagination.current,
                    pagination.pageSize,
                    openViewModal,
                    openEditModal,
                )}
                loading={loading}
            />

            {isModalOpen && (
                <ReviewRatingEditModal
                    visible={isModalOpen}
                    reviewRatingData={selectedReviewRating}
                    onSave={handleSubmit}
                    onClose={closeEditModal}
                />
            )}

            {isViewModalOpen && (
                <ReviewRatingViewModal
                    visible={isViewModalOpen}
                    reviewRatingData={selectedViewReview}
                    onClose={closeViewModal}
                />
            )}

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default ListReviewRating;
